"use client";

import { Montserrat } from "next/font/google";
import React from "react";
import Image from "next/image";
import { icons } from "@/icons";
import { data } from "@/data";
import { Smoothup as StaticSmoothup } from "@/components/common/animations";
import { useAPYContext } from "@/features/apy";
import { DownloadButtonPair } from "@/components/ui/DownloadButton";

// Import hero assets
import TossupImg from "@/assets/images/screenshots/app/tossup.webp";

const montserrat = Montserrat({ weight: "700", subsets: ["latin"] });
const montserrat2 = Montserrat({ weight: "600", subsets: ["latin"] });

// Import parallax elements directly to fix loading issues
import HeroParallaxMotion from "./HeroParallaxMotion";

export default function HeroSection() {
  const { apyRate } = useAPYContext();

  return (
    <section id="herosection" className="top-0 relative pt-24">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-custom-gradient" />
      <icons.lightarrays className="w-full absolute mt-[-20rem] md:mt-[-15rem] xl:mt-[-9rem]" />

      <div className="relative">
        {/* Hero tagline */}
        <StaticSmoothup className="z-30 flex justify-center mb-7">
          <p
            className={`${montserrat2.className} rounded-[144px] bg-white/30 border border-[#95D0DF] pt-[6px] pr-4 pb-[6px] pl-4 gap-2 font-semibold sm:text-[18px] sm:leading-[21px] text-[0.875rem] leading-[1.067rem] text-[#004248] uppercase`}
          >
            {data.herosection.herotagline}
          </p>
        </StaticSmoothup>

        {/* Hero heading and subheading */}
        <StaticSmoothup
          delay={0.2}
          className="flex flex-col items-center gap-4"
        >
          <h1
            className={`max-w-6xl z-30 px-8 tracking-[-0.04em] md:font-bold md:text-6xl md:leading-[72px] text-center text-5xl font-semibold leading-[45px] text-black ${montserrat.className}`}
          >
            {data.herosection.heading}
          </h1>
          <p className="max-w-sm md:max-w-[40rem] z-30 font-medium text-lg text-center text-black">
            {data.herosection.subheading}
          </p>
        </StaticSmoothup>

        {/* Features section */}
        <div className="mt-10 md:mt-14 relative z-30 px-5 xl:px-0">
          <StaticSmoothup
            delay={0.4}
            className="max-w-[55rem] z-30 mx-auto border rounded-xl md:rounded-full bg-black/40 border-white/50 md:flex backdrop-blur-[2px] justify-evenly items-center"
          >
            {apyRate && (
              <div className="flex flex-col py-4 px-6 lg:py-6 z-30 justify-center items-center">
                <p className="text-white text-center leading-5 font-medium">
                  Earn up to
                </p>
                <h2 className="bg-gradient-to-r from-[#7ACF3F] to-[#00C8F0] inline-block text-transparent bg-clip-text text-center text-4xl font-bold text-nowrap">
                  {apyRate}% APY*
                </h2>
              </div>
            )}
            <div className="flex flex-col py-4 px-6 lg:py-6 z-30 justify-center items-center">
              <p className="text-white text-center leading-5 font-medium">
                Invest in
              </p>
              <h2 className="bg-gradient-to-r from-[#7ACF3F] to-[#00C8F0] inline-block text-transparent bg-clip-text text-center text-4xl font-bold text-nowrap">
                ETFs
              </h2>
            </div>
            <div className="flex flex-col py-4 px-6 lg:py-6 z-30 justify-center items-center">
              <p className="text-white text-center leading-5 font-medium">
                Protected up to
              </p>
              <h2 className="bg-gradient-to-r from-[#7ACF3F] to-[#00C8F0] inline-block text-transparent bg-clip-text text-center text-4xl font-bold text-nowrap">
                $1M FDIC**
              </h2>
            </div>
          </StaticSmoothup>
        </div>

        {/* CTA Buttons */}
        <DownloadButtonPair
          className="px-5 xl:px-0 z-30 justify-center items-center mt-[3.2rem]"
          buttonClassName="z-30"
        />

        {/* Insurance text */}
        <div className="mt-5 flex flex-col items-center gap-4 h-[18rem] md:h-[28rem]">
          <p className="font-semibold text-sm px-6 xl:px-0 z-30 text-white text-center">
            {data.herosection.insurance}
          </p>
        </div>

        {/* Parallax background elements - Dynamic loaded for performance */}
        <div className="absolute bg-white/5 top-[33%] blur-[74px] backdrop-blur-[44px] w-[110%] h-[8.875rem] left-[-1.875rem]" />

        <HeroParallaxMotion />
      </div>

      {/* Hero phone image */}
      <Image
        src={TossupImg}
        alt="phones"
        width={768}
        height={685}
        className="left-1/2 translate-x-[-50%] absolute -bottom-[12rem] sm:-bottom-[26rem] md:-bottom-[17rem] z-30 w-[26rem] h-[24rem] p-5 sm:w-[48rem] sm:h-[43rem] drop-shadow-xl"
        priority
      />
    </section>
  );
}
